<template>
  <v-card v-if="show" class="chatbox">
    <v-card-title class="chatbox-header">
      <span>New Message</span>
      <v-btn icon size="small" @click="closeBox">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="chatbox-body">
      <v-autocomplete
        v-model="selected"
        :items="recipientList"
        item-title="name"
        item-value="userId"
        variant="outlined"
        density="compact"
        hide-details
        :menu-props="{ maxHeight: '140px', offsetY: true, attach: false }"
        label="Select recipient"
        @update:modelValue="onRecipientChange"
      >
        <template #item="{ item, props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-avatar size="24" class="mr-2">{{ item.raw.initials }}</v-avatar>
            </template>
            <v-list-item-title
              style="text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 210px;"
              >{{ item.raw.name }}</v-list-item-title
            >
          </v-list-item>
        </template>
      </v-autocomplete>

      <v-textarea
        v-model="msg"
        variant="outlined"
        label="Write a message..."
        rows="3"
        hide-details
        class="custom-textarea"
        @keydown.enter="handleKeyPress"
      ></v-textarea>

      <v-btn block :color="primaryColor" class="text-white" @click="sendMessage">
        Send
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script>
import chatService from "@/services/chatService";
import { mapGetters } from "vuex";

export default {
  name: "ChatBox",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      recipientList: [],
      selected: null,
      msg: "",
      primaryColor: 'primary',
    };
  },
  computed: {
    ...mapGetters("chatStore", ["recipientParties"]),
    ...mapGetters("auth0Store", ["user"]),
  },
  methods: {
    async init() {
      this.populateRecipientList(this.recipientParties);
    },
    populateRecipientList(parties) {
      this.recipientList = parties.map(party => ({
        id: party.id,
        name: party.name,
        initials: this.getInitials(party.name),
        userId: party.userId,
        connectedCompanyCode: party.connectedCompanyCode,
      }));
    },
    getInitials(name) {
      if (name === "") return "";

      return name
        .match(/\b(\w)/g)
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    closeBox() {
      this.$emit("close");
    },
    async handleKeyPress(event) {
      // Add newline on Shift+Enter otherwise send message
      if (!event.shiftKey) {
        event.preventDefault();
        await this.sendMessage();
      }
    },
    async sendMessage() {
      if (!this.msg || this.selected === null) {
        this.$root.$snackbar.message("Please select at least one recipient and write a message.");
        return;
      }

      try {
        const toParty = this.recipientList.find(r => r.userId === this.selected);
        if (toParty) {
          let channelUrl = await chatService.sendMessage(
            toParty,
            this.msg,
            `${this.user.firstName} ${this.user.lastName}`
          );
          this.$emit("message-sent", channelUrl);
          this.msg = "";
          this.selected = null;
        } else {
          this.$root.$snackbar.error("Failed to send the message. Please try again.");
        }
      } catch (error) {
        this.$root.$snackbar.error("Failed to send the message. Please try again.");
      }
    },
    onRecipientChange() {
      let selected = this.recipientList.find(x => x.userId == this.selected);
      this.$emit("recipient-changed", selected);
    },
  },
  watch: {
    show() {
      if (!this.show) this.selected = null;
    },
    recipientParties(newValue) {
      this.populateRecipientList(newValue);
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped>
.chatbox {
  position: fixed;
  bottom: 20px;
  right: 370px;
  width: 300px;
  z-index: 2000;
}

.chatbox-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1em;
  padding-bottom: 10px;
}

.chatbox-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.custom-textarea :deep(textarea) {
  font-size: small;
  line-height: 1.1rem;
}
</style>
